import { css } from "@emotion/css";
import { Popover, LegacyDataTable, LegacyDataTableBody, LegacyDataTableRow, LegacyDataTableRowColumn } from "@octopusdeploy/design-system-components";
import { TargetIcon } from "@octopusdeploy/design-system-icons";
import { space } from "@octopusdeploy/design-system-tokens";
import type { EnvironmentV2Resource, FeatureToggleEnvironmentResource, FeatureToggleResource, ProjectResource, TenantResource } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import _ from "lodash";
import { useCallback, useRef, useState } from "react";
import type * as React from "react";
import EnvironmentOrMissingChip from "~/areas/projects/components/FeatureToggles/EnvironmentOrMissingChip";
import { ExcludedTenantList, SegmentList, TagChipsFromTagNames } from "~/areas/projects/components/FeatureToggles/FeatureToggleEnvironment";
import { tenantChipListIncludingMissing } from "~/components/Chips";
import Chip from "~/components/Chips/Chip";
import { IconButtonWithTooltip } from "~/components/IconButtonWithTooltip";
import InternalLink from "~/components/Navigation/InternalLink";
import { useOctopusTheme } from "~/components/Theme";
import type { TagIndex } from "~/components/tagSets";
interface FeatureToggleTargetsButtonProps {
    project: ProjectResource;
    featureToggle: FeatureToggleResource;
    featureToggleEnvironment: FeatureToggleEnvironmentResource;
    environments: EnvironmentV2Resource[];
    tenants: TenantResource[];
    tenantTagIndex: TagIndex;
}
const styles = {
    wrapper: css({
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
    }),
    popoverContainer: css({
        margin: space[16],
    }),
    popoverSummaryText: css({
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: space[4],
        marginBottom: space[12],
    }),
    row: css({
        verticalAlign: "middle",
    }),
};
export default ({ project, featureToggle, featureToggleEnvironment, environments, tenants, tenantTagIndex }: FeatureToggleTargetsButtonProps) => {
    // if environment has no targeting, then return empty
    if (featureToggleEnvironment.ToggleStrategy === "EntireEnvironment") {
        return <span />;
    }
    const ref = useRef<HTMLDivElement | null>(null);
    const [buttonAnchor, setButtonAnchor] = useState<HTMLElement | null>(null);
    const handleClick = useCallback((_: React.MouseEvent) => setButtonAnchor(ref.current), [ref]);
    const theme = useOctopusTheme();
    const bullseyeIcon = <TargetIcon size={24} color={theme.iconNeutral}/>;
    return (<div className={styles.wrapper} ref={ref}>
            <IconButtonWithTooltip toolTipContent={"Show targeting details"} customIcon={bullseyeIcon} onClick={handleClick} accessibleName={"Show targeting details"}/>
            <Popover placement={"auto"} anchorEl={buttonAnchor} open={!!buttonAnchor} onClose={() => setButtonAnchor(null)}>
                <div className={styles.popoverContainer}>
                    <div className={styles.popoverSummaryText}>
                        The <InternalLink to={links.featureTogglePage.generateUrl({ spaceId: project.SpaceId, projectSlug: project.Slug, featureToggleSlug: featureToggle.Slug })}>{featureToggle.Name}</InternalLink> feature toggle targets the
                        following Tenants and/or Segments in <EnvironmentOrMissingChip environmentId={featureToggleEnvironment.DeploymentEnvironmentId} environments={environments}/>
                    </div>
                    <LegacyDataTable>
                        <LegacyDataTableBody>
                            {featureToggleEnvironment.TenantTargetingStrategy === "SpecificTenants" && (<>
                                    <LegacyDataTableRow>
                                        <LegacyDataTableRowColumn className={styles.row}>Tenants</LegacyDataTableRowColumn>
                                        <LegacyDataTableRowColumn>
                                            {featureToggleEnvironment.RolloutPercentage === null ? null : <Chip backgroundColor={theme.experimentBackground}>{featureToggleEnvironment.RolloutPercentage}% of tenants</Chip>}
                                            {tenantChipListIncludingMissing(tenants, featureToggleEnvironment.TenantIds)}
                                            {ExcludedTenantList({ excludedTenants: tenants.filter((tenant) => featureToggleEnvironment.ExcludedTenantIds.includes(tenant.Id)) })}
                                        </LegacyDataTableRowColumn>
                                    </LegacyDataTableRow>
                                    {(!_.isEmpty(featureToggleEnvironment.ExcludedTenantTags) || !_.isEmpty(featureToggleEnvironment.TenantTags)) && (<LegacyDataTableRow>
                                            <LegacyDataTableRowColumn className={styles.row}>Tenant tags</LegacyDataTableRowColumn>
                                            <LegacyDataTableRowColumn>
                                                <TagChipsFromTagNames canonicalTagNames={featureToggleEnvironment.TenantTags} tagIndex={tenantTagIndex}/>
                                                <TagChipsFromTagNames canonicalTagNames={featureToggleEnvironment.ExcludedTenantTags} tagIndex={tenantTagIndex} markAsRemoved={true}/>
                                            </LegacyDataTableRowColumn>
                                        </LegacyDataTableRow>)}
                                </>)}
                            {featureToggleEnvironment.Segments && featureToggleEnvironment.Segments.length > 0 && (<LegacyDataTableRow>
                                    <LegacyDataTableRowColumn className={styles.row}>Segments</LegacyDataTableRowColumn>
                                    <LegacyDataTableRowColumn>{SegmentList({ segments: featureToggleEnvironment.Segments })}</LegacyDataTableRowColumn>
                                </LegacyDataTableRow>)}
                        </LegacyDataTableBody>
                    </LegacyDataTable>
                </div>
            </Popover>
        </div>);
};
